/**
 * @file Resources for synchronizing a THREE.js camera with a maplibre/mabpox camera
 * @author Morgan Chesser for Round Three LLC
 * @license UNLICENSED
 * @typedef {import("maplibre-gl").Map} GLMap
 */

import * as THREE from "three"
import { GlobeControls } from "./GlobeControls.js"
import MathHelpers from "./MathHelpers.js"
import {
    Easing,
    Tween,
    Group as TweenGroup,
} from "./vendor/tweenjs/tween.esm.js"

// This import is purely for intellisense. npm maplibre-gl Map cannot be used, as we're using a custom fork of maplibregl provided by Maptiler.
// import { Map } from "maplibre-gl"

export default class CameraSynchronizer {
    /**
     *
     * @param {GlobeControls} threeControls
     * @param {GLMap} map
     */
    constructor(threeControls, map) {
        /**
         * three.js camera controller
         * @type {GlobeControls}
         */
        this.threeControls = threeControls

        /**
         * Maplibregl map
         * @type {GLMap}
         */
        this.map = map

        this.focusRadiusOverride = -1

        this.tweens = new TweenGroup()

        /**
         * @type {"globe" | "map"}
         */
        this.master = "globe"
    }

    handleResize(event) {
        // this.verticalResolution =
    }

    handleMeshRaise(mesh) {
        // if(mesh.userData.type !== "county") {
        //     return
        // }
        this.tweens.removeAll()

        const geometry = mesh.geometry
        if (!geometry) return

        // Calculate distance from a single vertex
        const positionAttribute = geometry.getAttribute("position")
        const vertex = new THREE.Vector3()
        vertex.fromBufferAttribute(positionAttribute, 0)
        mesh.localToWorld(vertex)
        vertex.sub(new THREE.Vector3(0, 50, 0))

        const startDist = vertex.length()
        const finalDist =
            startDist * (mesh.userData.clickedScale.x / mesh.scale.x)

        const threshold = 0.001

        if (Math.abs(this.threeControls.focusRadius - finalDist) < threshold)
            return

        new Tween(this.threeControls, this.tweens)
            .to({ focusRadius: finalDist }, 500)
            .easing(Easing.Quadratic.Out)
            .start()
    }

    updateTweens() {
        this.tweens.update()
    }

    update() {
        if (this.master === "globe") {
            this.syncMap()
        } else {
            this.syncGlobe()
        }
    }

    /**
     * Set map params using the globe as master
     */
    syncMap() {
        const trackData = this.threeControls.getTrackData()

        // Synchronize pitch
        const pitch = trackData.angle * MathHelpers.RAD2DEG

        const extraThetaOffset = this.threeControls.getExtraThetaOffset()

        // Synchronize latitude and longitude
        const lat = globePolarToLat(
            this.threeControls.getPolarAngle(),
            extraThetaOffset
        )

        const lng = globeAzimuthalToLng(this.threeControls.getAzimuthalAngle())

        // TODO: Make a list object and mutate it instead of constructing a new list every frame
        const center = [lng, lat]

        const distanceFromMap =
            trackData.distance - this.threeControls.focusRadius

        // We can get vertical FOV (the total frustum angle subtended by the vertical length of our screen) from the camera parameters
        const cameraFOV = this.threeControls.camera.fov

        // Convert globe "distance" to map "zoom level"
        const zoom = globeDistanceToMapZoom(
            cameraFOV,
            distanceFromMap,
            lat * MathHelpers.DEG2RAD
        )
        // Synchronize zoom
        this.map.jumpTo(
            { pitch, center, zoom },
            {
                cause: "CameraSynchronizer",
            }
        )
    }

    /**
     * Set globe params using map as master
     */
    syncGlobe() {
        const focusRadius = this.threeControls.focusRadius
        const extraThetaOffset = this.threeControls.getExtraThetaOffset()
        const cameraFOV = this.threeControls.camera.fov
        const { lng, lat } = this.map.getCenter()
        const zoom = this.map.getZoom()

        const distanceFromMap = mapZoomToGlobeDistance(
            cameraFOV,
            zoom,
            lat * MathHelpers.DEG2RAD
        )

        const trackDistance = distanceFromMap + focusRadius

        let trackPos = this.threeControls.invertCameraTrack(
            trackDistance,
            this.threeControls.minDistance
        )

        if (isNaN(trackPos)) {
            trackPos = 1
        }

        const globePolar = latToGlobePolar(lat, extraThetaOffset)
        const globeAzimuthal = lngToGlobeAzimuthal(lng)

        // Sync latitude and longitude
        this.threeControls.setPolarAngle(globePolar)
        this.threeControls.setAzimuthalAngle(globeAzimuthal)

        // Sync zoom
        this.threeControls.trackPos = trackPos
    }
}
/**
 * Convert from orbitControls spherical polar angle to latitude
 *
 * Inverse of latToGlobePolar
 * @param {Number} polarAngle - Polar angle in radians
 * @param {Number} extraThetaOffset
 * @returns
 */
export function globePolarToLat(polarAngle, extraThetaOffset) {
    return 90 - (polarAngle + extraThetaOffset) * MathHelpers.RAD2DEG
}

/**
 * Convert from latitude to orbitControls polar angle
 *
 * Inverse of glboePolarToLat
 * @param {Number} latitude - Latitude in degrees
 * @param {Number} extraThetaOffset
 * @returns
 */
export function latToGlobePolar(latitude, extraThetaOffset = 0) {
    return (90 - latitude) / MathHelpers.RAD2DEG - extraThetaOffset
}

/**
 * Convert from orbitControls spherical azimuthal angle to longitude
 *
 * Inverse of lngToGlobeAzimuthal
 * @param {Number} azimuthalAngle
 * @returns
 */
export function globeAzimuthalToLng(azimuthalAngle) {
    return -90 + azimuthalAngle * MathHelpers.RAD2DEG
}

/**
 * Convert from longitude to orbitControls spherical azimuthal angle
 *
 * Inverse of globeAzimuthalToLng
 * @param {Number} azimuthalAngle
 * @returns
 */
export function lngToGlobeAzimuthal(longitude) {
    return (longitude + 90) / MathHelpers.RAD2DEG
}

/**
 * Convert from sphere surface distance to map zoom level
 *
 * Inverse of mapZoomToGlobeDistance
 * @param {Number} cameraFOV
 * @param {Number} distance - Distance from the globe in arbitrary threejs units
 * @param {Number} latitude
 *
 */
function globeDistanceToMapZoom(cameraFOV, distance, latitude) {
    // Info related to converting map zoom level to screen-space distance
    // https://wiki.openstreetmap.org/wiki/Zoom_levels
    // https://wiki.openstreetmap.org/wiki/Slippy_map_tilenames#Resolution_and_Scale
    // map has 25 zoom levels, 0-24
    // 0 is all the way out, 24 is all the way in
    // Each zoom level in doubles the "screen size" of a tile

    // The resolution of the map in meters per pixel can be calculated with the following formula:
    // resolution (meters / pixel) = 156543.03 meters/pixel * cos(latitude) / (2 ^ zoomlevel)
    // It follows that, given a target resolution, the zoom level can be calculated from the following formula:
    // zoomLevel = log2(156543.03 meters/pixel * cos(latitude) / resolution)

    // Target resolution can be calculated using camera distance from the surface of the globe, horizontal fov of the camera, and horizontal pixel resolution of the screen.
    // First, we calculate what horizontal angle a known distance value subtends on the screen.
    // The entire globe is arbibrarily scaled to 100 "view units" (vunits) across
    const globeDiameter = 50 // vunits
    // The equatorial diameter of the earth is 12756274.000134174 meters
    const earthDiameter = 12756274.000134174 // meters
    const VUnitsPerMeter = globeDiameter / earthDiameter // Amount of vunits in one meter
    // View angle subtended by 1 meter can be found using trigonometry
    const viewRadiansPerMeter = 2 * Math.atan2(VUnitsPerMeter / 2, distance)

    const cameraVerticalFOV = cameraFOV * MathHelpers.DEG2RAD // Radians

    // We can get vertical screen resolution by hooking into onWindowResize events
    const screenResolution = window.innerHeight // pixels

    // Calculate the fraction of the screen that one meter takes up
    const screenFractionPerMeter = viewRadiansPerMeter / cameraVerticalFOV // Fraction of vertical screen. 0 is none, 1 is all of it. Unit is meter^-1

    // Finally, we can convert view angle per meter into pixels per meter
    const pixelsPerMeter = screenFractionPerMeter * screenResolution

    // Temp error correction for a weird fov-related effect I can't pin down
    // TODO: Figure out where this error is coming from
    const span = 1.5 - 0.76
    const t = (cameraVerticalFOV - 0.76) / span
    const errorCorrection = 1 - (1 - 0.85) * t

    // console.log(theta)
    return Math.log2(
        156543.03 * Math.cos(latitude) * pixelsPerMeter * errorCorrection
    )
}

/**
 * Convert map zoom level to sphere surface distance
 *
 * Inverse of globeDistanceToMapZoom
 * @param {Number} cameraFOV
 * @param {Number} zoom
 * @param {Number} latitude
 * @returns
 */
function mapZoomToGlobeDistance(cameraFOV, zoom, latitude) {
    // The entire globe is arbibrarily scaled to 100 "view units" (vunits) across
    const globeDiameter = 50 // vunits
    // The equatorial diameter of the earth is 12756274.000134174 meters
    const earthDiameter = 12756274.000134174 // meters
    const VUnitsPerMeter = globeDiameter / earthDiameter // Amount of vunits in one meter

    const cameraVerticalFOV = cameraFOV * MathHelpers.DEG2RAD
    const screenResolution = window.innerHeight // pixels

    const span = 1.5 - 0.76
    const t = (cameraVerticalFOV - 0.76) / span
    const errorCorrection = 1 - (1 - 0.85) * t

    const pixelsPerMeter =
        Math.pow(2, zoom) / (156543.03 * Math.cos(latitude) * errorCorrection)

    const screenFractionPerMeter = pixelsPerMeter / screenResolution
    const viewRadiansPerMeter = screenFractionPerMeter * cameraVerticalFOV
    const distance = VUnitsPerMeter / (2 * Math.tan(viewRadiansPerMeter / 2))
    return distance
}
